import { Button, Switch, Typography } from '@mui/material';
import { MdPrint } from '@teamworksdev/icons';
import { Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { AthleteSelect } from '../../components/layout/athlete-select';
import { Page } from '../../components/layout/page';
import { LabeledLink, PageHeader } from '../../components/layout/page-header';
import { TeamSelect } from '../../components/layout/TeamSelect';
import { useFlags } from '../../context/featureFlag';
import { PlanProvider, usePlanContext } from './context/PlanContext';

// wrapper to allow us to access the usePlanContext hook in the PlanPageContent
export function PlanPage() {
  return (
    <PlanProvider>
      <Page>
        <PlanPageContent />
      </Page>
    </PlanProvider>
  );
}

function PlanPageContent() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const state = useRouterState();
  const {
    teamId,
    selectedAthlete,
    setSelectedAthlete,
    showCanceled,
    setShowCanceled,
    showAdditionalTeams,
    setShowAdditionalTeams,
    athletes,
    setPrintDrawerOpen,
  } = usePlanContext();
  const { showAdditionalTeamsFlag } = useFlags();

  // Check if we're on the calendar page - athlete selector is shown in the sidebar instead
  const isCalendarPage = state.location.pathname.includes('/calendar');

  const showAdditionalTeamsToggle =
    !!selectedAthlete && showAdditionalTeamsFlag;

  // Persist the athleteId search param when navigating between tabs
  const searchParams = selectedAthlete ? { athleteId: selectedAthlete.id } : {};
  const navigationLinks: LabeledLink[] = [
    {
      to: '/$teamId/plan/timeline',
      label: t('timeline'),
      params: { teamId },
      search: searchParams,
    },
    {
      to: '/$teamId/plan/calendar',
      label: t('calendar'),
      params: { teamId },
      search: searchParams,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('annualPlanner')}
        rightSlot={
          <div className="flex gap-2 items-center">
            <div className="flex items-center">
              <Switch
                size="small"
                checked={showCanceled}
                onChange={(e) => setShowCanceled(e.target.checked)}
              />
              <Typography variant="body2" className="ml-2">
                {t(
                  isCalendarPage
                    ? 'showCanceledWorkouts'
                    : 'showCanceledBlocks',
                )}
              </Typography>
            </div>
            {showAdditionalTeamsToggle && (
              <div className="flex items-center">
                <Switch
                  size="small"
                  checked={showAdditionalTeams}
                  onChange={(e) => setShowAdditionalTeams(e.target.checked)}
                />
                <Typography variant="body2" className="ml-2">
                  {t('showAdditionalTeams')}
                </Typography>
              </div>
            )}
            <TeamSelect
              teamId={teamId}
              onSelect={(newTeamId) => {
                // Keep the current sub-path (timeline or calendar) when switching teams
                const [, afterTeamId] = state.location.pathname.split(
                  `/${teamId}/plan`,
                );
                const subPath = afterTeamId || '/timeline';
                const newPath = `/${newTeamId}/plan${subPath}`;
                navigate({
                  to: newPath,
                  params: { teamId: newTeamId },
                  replace: true,
                });
              }}
              sx={{ mt: 0 }}
            />
            {isCalendarPage && (
              <Button
                size="small"
                onClick={() => setPrintDrawerOpen(true)}
                variant="outlined"
                className="flex items-center px-2"
                aria-label={t('print')}
                sx={{ height: 36 }}
              >
                <MdPrint size={20} aria-hidden="true" />
              </Button>
            )}
            {!isCalendarPage && (
              <AthleteSelect
                athletes={athletes ?? []}
                selectedAthlete={selectedAthlete}
                onChange={setSelectedAthlete}
              />
            )}
          </div>
        }
        navigationLinks={navigationLinks}
      />
      <Outlet />
    </>
  );
}
